import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ShieldCheck, KeyRound } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { getPasswordComplexityErrors, PASSWORD_HINT } from '@/utils/passwordValidation';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenant();

  // Get the intended destination - if it's an absolute path without tenant, make it tenant-relative
  const getRedirectPath = () => {
    const fromPath = location.state?.from?.pathname;
    if (fromPath) {
      return fromPath; // Already a full path with tenant prefix
    }
    return tenantPath('/dashboard'); // Default to tenant's dashboard
  };

  const from = getRedirectPath();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' | 'mfa' | 'force_password_change'
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [mfaState, setMfaState] = useState({ challengeId: '', code: '', emailHint: '' });
  const [newPassword, setNewPassword] = useState({ value: '', confirm: '' });

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + from;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const transferGuestCart = async () => {
    try {
      const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
      if (guestCart.length === 0) return;

      for (const item of guestCart) {
        try {
          await api.post('/cart', {
            product_id: item.product_id,
            quantity: item.quantity || 1,
            configuration: item.configuration || {}
          });
        } catch (e) {
          console.warn('Failed to transfer guest cart item to account:', item.product_id, e);
        }
      }
      localStorage.removeItem('guestCart');
    } catch (e) {
      console.warn('Failed to transfer guest cart:', e);
    }
  };

  const finishLogin = async (user) => {
    await transferGuestCart();
    toast.success(`Welcome back, ${user.name}!`);
    navigate(from, { state: { user }, replace: true });
  };

  const handleAuthResult = (data) => {
    if (data.mfa_required) {
      setMfaState({ challengeId: data.challenge_id, code: '', emailHint: data.email_hint || '' });
      setStep('mfa');
      return;
    }
    if (data.must_change_password) {
      setStep('force_password_change');
      return;
    }
    finishLogin(data);
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.post('/auth/login', formData);
      handleAuthResult(response.data);
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    if (!mfaState.code || mfaState.code.length !== 6) {
      toast.error('Enter the 6-digit code sent to your email');
      return;
    }
    try {
      setIsLoading(true);
      const response = await api.post('/auth/mfa/verify-login', {
        challenge_id: mfaState.challengeId,
        code: mfaState.code
      });
      handleAuthResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/mfa/resend-login-code', {
        challenge_id: mfaState.challengeId
      });
      setMfaState((prev) => ({ ...prev, challengeId: response.data.challenge_id, code: '' }));
      toast.success('A new code has been sent to your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not resend code. Please sign in again.');
      setStep('credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForcePasswordChange = async (e) => {
    e.preventDefault();
    const pwErrors = getPasswordComplexityErrors(newPassword.value);
    if (pwErrors.length > 0) {
      toast.error(`Password must contain ${pwErrors.join(', ')}.`);
      return;
    }
    if (newPassword.value !== newPassword.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setIsLoading(true);
      await api.post('/auth/change-password', {
        current_password: formData.password,
        new_password: newPassword.value
      });
      toast.success('Password updated successfully.');
      const me = await api.get('/auth/me');
      finishLogin(me.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <img src="/logo.png" alt="Golfshop.fit" className="h-20 w-20 mx-auto mb-4 rounded-full object-cover" data-testid="login-title" />
          <p className="text-[#5A6B5C] text-lg">Sign in to access your account</p>
        </div>

        <div className="bg-white p-8 rounded-xl border border-[#E8E4DC] shadow-sm">
          {step === 'credentials' && (
            <>
              {/* Email/Password Login Form */}
              <form onSubmit={handleEmailLogin} className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="email" className="text-[#2C3E2D] text-sm mb-2 block">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-[#F5F3EF] border-[#E8E4DC] text-[#2C3E2D] focus:border-[#4A6741] focus:ring-[#4A6741]"
                    placeholder="you@example.com"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#2C3E2D] text-sm mb-2 block">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full bg-[#F5F3EF] border-[#E8E4DC] text-[#2C3E2D] focus:border-[#4A6741] focus:ring-[#4A6741]"
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 px-8 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3A5733] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="email-login-button"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>

                <Link
                  to={tenantPath('/forgot-password')}
                  className="block text-center text-[#4A6741] hover:text-[#3A5733] text-sm mt-3 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </form>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E8E4DC]"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-[#5A6B5C]">Or continue with</span>
                </div>
              </div>

              {/* Google OAuth */}
              <button
                onClick={handleGoogleLogin}
                className="w-full h-12 px-8 bg-transparent border border-[#E8E4DC] text-[#2C3E2D] font-bold uppercase tracking-wider rounded-lg hover:border-[#4A6741] hover:text-[#4A6741] transition-all flex items-center justify-center space-x-3"
                data-testid="google-login-button"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google</span>
              </button>

              <p className="text-center text-[#5A6B5C] text-sm mt-6">
                Don't have an account?{' '}
                <Link to={tenantPath('/register')} className="text-[#4A6741] hover:text-[#3A5733] font-medium transition-colors">
                  Sign Up
                </Link>
              </p>

              <p className="text-[#5A6B5C] text-xs text-center mt-4">
                By continuing, you agree to our Terms of Service and Privacy Policy
              </p>
            </>
          )}

          {step === 'mfa' && (
            <>
              <div className="h-12 w-12 bg-[#4A6741]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-6 w-6 text-[#4A6741]" />
              </div>
              <h2 className="text-xl font-bold text-[#2C3E2D] text-center mb-2" data-testid="mfa-title">
                Enter Verification Code
              </h2>
              <p className="text-[#5A6B5C] text-center text-sm mb-6">
                We sent a 6-digit code to {mfaState.emailHint || 'your email'}
              </p>

              <form onSubmit={handleVerifyMfa} className="space-y-4">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={mfaState.code}
                  onChange={(e) => setMfaState(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '') }))}
                  className="w-full bg-[#F5F3EF] border-[#E8E4DC] text-center text-2xl tracking-[0.5em]"
                  data-testid="mfa-code-input"
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 px-8 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3A5733] transition-all disabled:opacity-50"
                  data-testid="mfa-verify-submit"
                >
                  {isLoading ? 'Verifying...' : 'Verify & Sign In'}
                </button>
              </form>

              <div className="mt-6 text-center space-y-2">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading}
                  className="text-[#4A6741] text-sm font-medium hover:underline disabled:opacity-50"
                  data-testid="mfa-resend-code-btn"
                >
                  Resend code
                </button>
                <div>
                  <button
                    type="button"
                    onClick={() => setStep('credentials')}
                    className="text-[#5A6B5C] text-sm hover:text-[#4A6741]"
                    data-testid="mfa-back-btn"
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            </>
          )}

          {step === 'force_password_change' && (
            <>
              <div className="h-12 w-12 bg-[#D4A55A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <KeyRound className="h-6 w-6 text-[#D4A55A]" />
              </div>
              <h2 className="text-xl font-bold text-[#2C3E2D] text-center mb-2" data-testid="force-password-change-title">
                Update Your Password
              </h2>
              <p className="text-[#5A6B5C] text-center text-sm mb-6">
                For security, please set a new password before continuing.
              </p>

              <form onSubmit={handleForcePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="text-[#2C3E2D] text-sm mb-2 block">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword.value}
                    onChange={(e) => setNewPassword(prev => ({ ...prev, value: e.target.value }))}
                    className="w-full bg-[#F5F3EF] border-[#E8E4DC]"
                    data-testid="force-password-new-input"
                  />
                  <p className="text-xs text-[#5A6B5C] mt-1">{PASSWORD_HINT}</p>
                </div>
                <div>
                  <Label htmlFor="confirm-new-password" className="text-[#2C3E2D] text-sm mb-2 block">Confirm New Password</Label>
                  <Input
                    id="confirm-new-password"
                    type="password"
                    value={newPassword.confirm}
                    onChange={(e) => setNewPassword(prev => ({ ...prev, confirm: e.target.value }))}
                    className="w-full bg-[#F5F3EF] border-[#E8E4DC]"
                    data-testid="force-password-confirm-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 px-8 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3A5733] transition-all disabled:opacity-50"
                  data-testid="force-password-submit"
                >
                  {isLoading ? 'Updating...' : 'Update Password & Continue'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
